import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* c8 ignore start */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Alert, Button, Spacer, Typography, TextField, Select, MenuItem, Card, Stack, useSnackbar } from '@emburse/embark-core';
import { $t, useTranslation } from '../../hooks/useTranslation';
import useAccountConfig from '../../hooks/useAccountConfig';
import { defaultValidation, validateRoutingNumber, validateConfirmRoutingNumber, validateBankAccountNumber, validateConfirmBankAccountNumber, validatePhone } from '../../util/formValidation';
import { getStateProvinces } from '../../api/api';
import { getEmployeeAchAccount, createEmployeeAchAccount } from '../../api/employeeAchAccount';
import getBankDetailsByRoutingNumber from '../../api/bankDetails';
import getAchConfig from '../../api/achConfig';
import { AccountType } from '../../interfaces/EmpACHAccount';
import StateProvince from '../../interfaces/StateProvince';
import { EmpType } from '../../interfaces/Employee';
const EMPLOYEE_BANK_ACCOUNT_SELECT_FIELDS = ['accountType', 'stateProvince'];
const EMPLOYEE_BANK_ACCOUNT_TEXT_FIELDS = [
    'routingNumber',
    'confirmRoutingNumber',
    'bankAccountNumber',
    'confirmBankAccountNumber',
    'nameOnAccount',
    'address',
    'city',
    'postalCode',
    'phone'
];
const employeeBankFields = {
    ...Object.fromEntries(EMPLOYEE_BANK_ACCOUNT_SELECT_FIELDS.map((field) => [field, 'select'])),
    ...Object.fromEntries(EMPLOYEE_BANK_ACCOUNT_TEXT_FIELDS.map((field) => [field, 'text']))
};
const bankFieldStyle = { width: '260px' };
const initialEmployeeBankFormState = {
    accountType: { value: '', validationText: '' },
    routingNumber: { value: '', validationText: '' },
    confirmRoutingNumber: { value: '', validationText: '' },
    bankAccountNumber: { value: '', validationText: '' },
    confirmBankAccountNumber: { value: '', validationText: '' },
    nameOnAccount: { value: '', validationText: '' },
    address: { value: '', validationText: '' },
    city: { value: '', validationText: '' },
    stateProvince: { value: '', validationText: '' },
    postalCode: { value: '', validationText: '' },
    phone: { value: '', validationText: '' }
};
function getValidationFunction(field, newValue, compareValue) {
    switch (field) {
        case 'routingNumber':
            return validateRoutingNumber(newValue);
        case 'confirmRoutingNumber':
            return validateConfirmRoutingNumber(newValue, compareValue || '');
        case 'bankAccountNumber':
            return validateBankAccountNumber(newValue);
        case 'confirmBankAccountNumber':
            return validateConfirmBankAccountNumber(newValue, compareValue || '');
        case 'phone':
            return validatePhone(newValue);
        default:
            return defaultValidation(newValue);
    }
}
const US_STATE_MAX_PK = 56;
const NA_STATE_PK = 69;
const filterStateProvinces = (stateProvincesResponse) => stateProvincesResponse
    .map((state) => new StateProvince(state))
    .filter((state) => {
    // Filter out non-US states except for the N/A state option
    const statePk = parseInt(state.PK, 10);
    return statePk <= US_STATE_MAX_PK || statePk === NA_STATE_PK;
});
function EmployeeAchConfig() {
    useTranslation();
    const { accountConfig: AccountConfig } = useAccountConfig();
    const { enqueueSnackbar } = useSnackbar();
    const handleError = useCallback((err) => {
        enqueueSnackbar(err.message, { variant: 'alert', AlertProps: { severity: 'error' } });
    }, [enqueueSnackbar]);
    const [isFormChanged, setIsFormChanged] = useState(false);
    const [formState, setFormState] = useState(initialEmployeeBankFormState);
    const [initialFormState, setInitialFormState] = useState(null);
    const [isInitialFetchComplete, setIsInitialFetchComplete] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [achAccount, setAchAccount] = useState(null);
    const [bankDetails, setBankDetails] = useState(null);
    const [isBankSearched, setIsBankSearched] = useState(false);
    const [stateProvinces, setStateProvinces] = useState([]);
    const [achConfig, setAchConfig] = useState(null);
    const [readOnlyText, setReadOnlyText] = useState('');
    const fetchMappedStateProvinces = useCallback(async () => {
        try {
            const stateProvincesResponse = await getStateProvinces();
            const filteredStateProvinces = filterStateProvinces(stateProvincesResponse);
            // Create a mapping of state abbreviations to PKs for the dto to use
            const abbreviationToPkMap = {};
            filteredStateProvinces.forEach((state) => {
                abbreviationToPkMap[state.Data] = state.PK;
            });
            setStateProvinces(filteredStateProvinces);
        }
        catch (err) {
            handleError({ message: $t('An error occurred fetching mapped state provinces.') });
        }
    }, [handleError]);
    const DEFAULT_READ_ONLY_TEXT = $t(`Account information is maintained through an internal company process. 
        Please contact your Certify Administrators to request a change.`);
    const fetchAchConfig = useCallback(async () => {
        try {
            const configResponse = await getAchConfig();
            const configFlags = await configResponse.json();
            setAchConfig(configFlags);
            // Determine redirect logic: Redirect to home if ACH configuration is not active or
            // the user is not allowed to edit ACH information and is delegating or masquerading
            const shouldRedirectHome = !configFlags.IsActiveCompanyAchConfiguration ||
                (configFlags.IsAchEmployeeEditOnly &&
                    AccountConfig.UIFlags.IsDelegatingOrMasquerading);
            if (shouldRedirectHome) {
                window.location.href = 'Home.aspx';
            }
            // Determine read-only state
            let readOnly = false;
            let readOnlyLabelText = '';
            const role = AccountConfig.Emp_Type;
            // Determine read-only state
            if (configFlags.IsDirectDepositAccountsReadOnly) {
                readOnly = true;
                readOnlyLabelText = DEFAULT_READ_ONLY_TEXT;
            }
            else if (configFlags.IsAccountantOnlyAchAccount &&
                !(role === EmpType.Accountant || AccountConfig.IsAdmin)) {
                readOnly = true;
                readOnlyLabelText = DEFAULT_READ_ONLY_TEXT;
            }
            else if (configFlags.IsAchEmployeeEditOnly && AccountConfig.UIFlags.IsMasquerading) {
                readOnly = true;
                readOnlyLabelText = $t('Account information may only be edited by the user to which it belongs.');
                // //to do: CER-19863 will handle navigating from the manage users pages
                // } else if (EmpIdFromManageUsers !== AccountConfig.Emp.PK) {
                //     readOnly = true;
            }
            setIsReadOnly(readOnly);
            setReadOnlyText(readOnlyLabelText);
        }
        catch (error) {
            handleError({ message: $t('An error occurred fetching ACH configuration.') });
        }
    }, [
        AccountConfig.Emp_Type,
        AccountConfig.UIFlags.IsDelegatingOrMasquerading,
        AccountConfig.UIFlags.IsMasquerading,
        AccountConfig.IsAdmin,
        DEFAULT_READ_ONLY_TEXT,
        handleError
    ]);
    const fetchEmployeeInformation = useCallback(async () => {
        try {
            const accountResponse = await getEmployeeAchAccount();
            const employeeAccount = await accountResponse.json();
            if (!employeeAccount || employeeAccount.Id <= 0) {
                return null;
            }
            return employeeAccount;
        }
        catch (error) {
            handleError({
                message: $t('An error occurred fetching employee direct deposit information.')
            });
            return null; // Return null in case of error
        }
    }, [handleError]);
    const fetchBankingInformation = useCallback(async () => {
        try {
            const bankResponse = await getBankDetailsByRoutingNumber(formState.routingNumber.value);
            const newBankDetails = await bankResponse.json();
            if (!newBankDetails.IsRoutingNumberCheckSumValid ||
                !newBankDetails.IsFoundInBankResource) {
                setBankDetails(null);
                return;
            }
            setBankDetails(newBankDetails);
        }
        catch (err) {
            handleError({ message: $t('An error occurred fetching bank details.') });
        }
    }, [handleError, formState.routingNumber.value]);
    const validateAndSetFormState = (field, newValue, relatedField) => {
        setFormState((prevState) => {
            const currentValidationText = getValidationFunction(field.toString(), newValue, relatedField ? prevState[relatedField].value : undefined);
            const updatedState = {
                ...prevState,
                [field]: {
                    value: newValue,
                    validationText: currentValidationText
                }
            };
            if (relatedField && prevState[relatedField].value.length > 0) {
                const relatedValidationText = getValidationFunction(relatedField.toString(), prevState[relatedField].value, newValue);
                if (prevState[relatedField].validationText !== relatedValidationText) {
                    updatedState[relatedField] = {
                        ...prevState[relatedField],
                        validationText: relatedValidationText
                    };
                }
            }
            // Only return the updated state if there are changes
            if (prevState[field].value !== newValue ||
                prevState[field].validationText !== currentValidationText ||
                (relatedField &&
                    prevState[relatedField].validationText !==
                        updatedState[relatedField].validationText)) {
                return updatedState;
            }
            return prevState;
        });
    };
    //* **** Event Handlers ******/
    const accountTypeChangeHandler = (e) => {
        const newAccountType = e.target.value;
        validateAndSetFormState('accountType', newAccountType);
    };
    const routingNumberChangeHandler = (e) => {
        validateAndSetFormState('routingNumber', e.target.value, 'confirmRoutingNumber');
    };
    const confirmRoutingNumberChangeHandler = (e) => {
        validateAndSetFormState('confirmRoutingNumber', e.target.value, 'routingNumber');
    };
    const bankAccountChangeHandler = (e) => {
        validateAndSetFormState('bankAccountNumber', e.target.value, 'confirmBankAccountNumber');
    };
    const confirmBankAccountChangeHandler = (e) => {
        validateAndSetFormState('confirmBankAccountNumber', e.target.value, 'bankAccountNumber');
    };
    const handleChange = (field) => (e) => {
        const newValue = e.target.value;
        validateAndSetFormState(field, newValue);
    };
    const validateForm = () => Object.values(formState).every((field) => field.value !== '' && field.validationText === '');
    const createField = (value) => ({ value, validationText: '' });
    const generateFormState = useCallback((account) => ({
        accountType: createField(account.AccountTypeId === 0 ? 'Checking' : 'Savings'),
        routingNumber: createField(account.RoutingNumber),
        confirmRoutingNumber: createField(account.RoutingNumber),
        bankAccountNumber: createField(account.BankAccount),
        confirmBankAccountNumber: createField(account.BankAccount),
        nameOnAccount: createField(account.Name),
        address: createField(account.Address),
        city: createField(account.City),
        stateProvince: createField(account.StateProvinceId.toString()),
        postalCode: createField(account.PostalCode),
        phone: createField(account.Phone)
    }), []);
    const handleEmployeeAchAccount = async () => {
        if (!validateForm()) {
            return;
        }
        // we may handle the employee ID differently in the future based on access control
        const employeeAccount = {
            Id: achAccount ? achAccount.Id : null,
            EmployeeId: AccountConfig.Emp.PK,
            AccountTypeId: formState.accountType.value === AccountType.Checking ? 0 : 1,
            RoutingNumber: formState.routingNumber.value,
            BankAccount: formState.bankAccountNumber.value,
            Name: formState.nameOnAccount.value,
            Address: formState.address.value,
            City: formState.city.value,
            StateProvinceId: parseInt(formState.stateProvince.value, 10) || null,
            PostalCode: formState.postalCode.value,
            Phone: formState.phone.value,
            Authorization: true,
            TaxpayerID: ''
        };
        try {
            const createResponse = await createEmployeeAchAccount(employeeAccount);
            // Handling form submission and updating form state
            const newEmployeeAccount = await createResponse.json();
            const updatedFormState = generateFormState(newEmployeeAccount);
            setFormState(updatedFormState);
            setInitialFormState(updatedFormState);
            setAchAccount(newEmployeeAccount);
            setIsBankSearched(false);
            setIsFormChanged(false);
            enqueueSnackbar($t('Direct deposit account information updated successfully.'), {
                variant: 'success'
            });
        }
        catch (error) {
            handleError({
                message: $t('An error occurred creating employee direct deposit information.')
            });
        }
    };
    const isBankLookupRequired = useCallback(() => {
        if (!achConfig) {
            return false;
        }
        const shouldSkipBankLookup = achConfig.IsInternalCertifyAchAccount || !achConfig.IsAchActive;
        if (shouldSkipBankLookup) {
            return false;
        }
        const isMaskedRoutingNumber = (routingNumber) => routingNumber.includes('*');
        const { routingNumber, confirmRoutingNumber } = formState;
        return (routingNumber.value &&
            confirmRoutingNumber.value &&
            routingNumber.value === confirmRoutingNumber.value &&
            !isMaskedRoutingNumber(routingNumber.value));
    }, [formState, achConfig]);
    useEffect(() => {
        fetchAchConfig();
    }, [fetchAchConfig]);
    useEffect(() => {
        if (isReadOnly) {
            return;
        }
        const hasChanged = JSON.stringify(formState) !== JSON.stringify(initialFormState);
        setIsFormChanged(hasChanged);
    }, [formState, initialFormState, isReadOnly]);
    useEffect(() => {
        if (isReadOnly) {
            return;
        }
        const fetchData = async () => {
            const fetchedData = await fetchEmployeeInformation();
            if (fetchedData) {
                const formStateData = generateFormState(fetchedData);
                setFormState(formStateData);
                setInitialFormState(formStateData);
                setAchAccount(fetchedData);
            }
            await fetchMappedStateProvinces();
            await fetchAchConfig();
            setIsInitialFetchComplete(true);
        };
        fetchData();
    }, [
        fetchEmployeeInformation,
        fetchMappedStateProvinces,
        fetchAchConfig,
        isReadOnly,
        generateFormState
    ]);
    useMemo(() => {
        if (isReadOnly) {
            return;
        }
        if (isInitialFetchComplete && isBankLookupRequired()) {
            fetchBankingInformation();
            setIsBankSearched(true);
        }
        else {
            setBankDetails(null);
        }
    }, [isInitialFetchComplete, isBankLookupRequired, fetchBankingInformation, isReadOnly]);
    if (isReadOnly) {
        return (_jsx(Stack, { children: _jsx(Alert, { severity: "info", children: readOnlyText }) }));
    }
    return (_jsxs("form", { children: [_jsxs(Card, { "data-qa": "employee-ach-form", children: [_jsx(Typography, { variant: "h4", children: $t('Direct Deposit Account Information') }), _jsx(Spacer, { size: 8 }), _jsxs(Typography, { children: [$t('Enter your bank account information to enable direct deposit for reimbursements.'), _jsx("br", {}), $t('Be sure to enter address information that is associated with the bank account.')] }), _jsx(Spacer, { size: 20 }), _jsxs(Stack, { alignItems: "baseline", spacing: 2, children: [_jsx(Select, { id: "accountType", label: "Account Type", value: formState.accountType.value, helperText: formState.accountType.validationText, error: !!formState.accountType.validationText, onChange: accountTypeChangeHandler, style: bankFieldStyle, children: Object.entries(AccountType).map(([key, value]) => (_jsx(MenuItem, { value: value, children: value }, key))) }), _jsxs(Stack, { direction: "row", spacing: 2, alignItems: "flex-start", children: [_jsx(TextField, { id: "routingNumber", label: "Routing Number", value: formState.routingNumber.value, onChange: routingNumberChangeHandler, error: !!formState.routingNumber.validationText, helperText: formState.routingNumber.validationText, style: bankFieldStyle }), _jsx(TextField, { id: "confirmRoutingNumber", label: "Confirm Routing Number", value: formState.confirmRoutingNumber.value, onChange: confirmRoutingNumberChangeHandler, error: !!formState.confirmRoutingNumber.validationText, helperText: formState.confirmRoutingNumber.validationText, style: bankFieldStyle })] }), _jsxs(Stack, { direction: "row", spacing: 2, alignItems: "flex-start", children: [_jsx(TextField, { id: "bankAccountNumber", label: "Bank Account Number", value: formState.bankAccountNumber.value, onChange: bankAccountChangeHandler, error: !!formState.bankAccountNumber.validationText, helperText: formState.bankAccountNumber.validationText, style: bankFieldStyle }), _jsx(TextField, { id: "confirmBankAccountNumber", label: "Confirm Bank Account Number", value: formState.confirmBankAccountNumber.value, onChange: confirmBankAccountChangeHandler, error: !!formState.confirmBankAccountNumber.validationText, helperText: formState.confirmBankAccountNumber.validationText, style: bankFieldStyle })] }), bankDetails ? (_jsxs(Stack, { direction: "row", spacing: 6, children: [_jsx(Typography, { variant: "subtitle2", style: { fontWeight: 'bold' }, children: "Bank Information" }), _jsxs(Typography, { variant: "caption", children: [bankDetails.BankName, _jsx("br", {}), bankDetails.BankAddress, _jsx("br", {}), bankDetails.BankCity, ",", bankDetails.BankState, ' ', bankDetails.BankZipCode, _jsx("br", {}), bankDetails.BankPhone] })] })) : (isBankSearched && (_jsx(Stack, { children: _jsx(Alert, { severity: "info", children: $t(`The Bank Routing Number you entered does not match
                                a US bank. Please check your entry and try again.
                                If you are certain the Bank Routing Number is correct,
                                click Save and it will be used without verification.`) }) }))), _jsx(TextField, { id: "nameOnAccount", label: "Name On Account", value: formState.nameOnAccount.value, onChange: handleChange('nameOnAccount'), error: !!formState.nameOnAccount.validationText, helperText: formState.nameOnAccount.validationText, style: bankFieldStyle }), _jsx(TextField, { id: "address", label: "Address", value: formState.address.value, onChange: handleChange('address'), error: !!formState.address.validationText, helperText: formState.address.validationText, style: bankFieldStyle }), _jsx(TextField, { id: "city", label: "City", value: formState.city.value, onChange: handleChange('city'), error: !!formState.city.validationText, helperText: formState.city.validationText, style: bankFieldStyle }), _jsx(Select, { id: "stateProvince", label: "State", value: formState.stateProvince.value, helperText: formState.stateProvince.validationText, error: !!formState.stateProvince.validationText, onChange: handleChange('stateProvince'), style: bankFieldStyle, children: stateProvinces.map(({ PK, Name }) => (_jsx(MenuItem, { value: PK, children: Name }, PK))) }), _jsx(TextField, { id: "postalCode", label: "Postal Code", value: formState.postalCode.value, onChange: handleChange('postalCode'), error: !!formState.postalCode.validationText, helperText: formState.postalCode.validationText, style: bankFieldStyle }), _jsx(TextField, { id: "phone", label: "Phone", value: formState.phone.value, onChange: handleChange('phone'), error: !!formState.phone.validationText, helperText: formState.phone.validationText, style: bankFieldStyle })] })] }), _jsx(Spacer, { size: 16 }), _jsxs(Stack, { alignItems: "baseline", spacing: 2, children: [_jsx(Alert, { severity: "warning", children: $t(`By clicking save, you authorize your employer or their service provider to send electronic fund
                    transfers to the bank account you identified above.
                    Your employer will transfer funds to Emburse Certify, in its role as your agent, who will then
                    initiate an electronic transfer to you.
                    Payment to Emburse Certify by your employer extinguishes your employer&apos;s obligation to pay you.
                    Your only recourse for payment will be against Emburse Certify.`) }), _jsxs(Stack, { direction: "row", spacing: 2, children: [_jsx(Button, { variant: "outlined", onClick: () => {
                                    window.location.href = 'Home.aspx';
                                }, "data-qa": "employee-ach-cancel", children: $t('Cancel') }), _jsx(Button, { type: "button", onClick: handleEmployeeAchAccount, disabled: isReadOnly ||
                                    !isFormChanged ||
                                    !isInitialFetchComplete ||
                                    !validateForm(), title: isReadOnly ? readOnlyText : '', "data-qa": "employee-ach-save", children: "Save" })] })] })] }));
}
export default EmployeeAchConfig;
